import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LOCATIONS } from "@/lib/suppliers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Sparkles,
  TrendingUp,
  AlertTriangle,
  ShieldAlert,
  Lightbulb,
  Loader2,
  RefreshCw,
  BarChart3,
} from "lucide-react";

/**
 * AIAnalysisPanel — sits at the top of the Private Portal.
 * Generates a fresh AI-powered Greek-language business report (with metrics
 * aggregated server-side) for the selected store/period.
 */
export default function AIAnalysisPanel() {
  const [store, setStore] = useState("Global");
  const [period, setPeriod] = useState("month");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const generate = async () => {
    setBusy(true);
    const t = toast.loading("Generating AI financial analysis…");
    try {
      const { data } = await api.post(
        "/portal/ai-analysis",
        { store, period },
        { timeout: 120000 }
      );
      setReport(data);
      toast.success(
        data.ai_source === "llm"
          ? "AI report ready"
          : data.ai_source === "empty"
          ? "Insufficient data — no insights yet"
          : "Fallback report ready (LLM unavailable)",
        { id: t }
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate report", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const m = report?.metrics;
  const r = report?.report;

  return (
    <Card
      className="p-6 mb-6 border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/60 via-white to-violet-50/40"
      data-testid="ai-analysis-panel"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white flex items-center justify-center shadow-md">
            <Sparkles className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 leading-tight">
              🤖 AI Automated Financial Analysis
            </h2>
            <p className="text-xs text-slate-500 leading-tight">
              Αυτοματοποιημένη Χρηματοοικονομική Ανάλυση · Greek-language LLM insights
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select value={store} onValueChange={setStore}>
            <SelectTrigger className="min-h-[40px] w-44" data-testid="ai-store-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Global" data-testid="ai-store-opt-global">Global · Όλα</SelectItem>
              {LOCATIONS.map((l) => (
                <SelectItem key={l} value={l} data-testid={`ai-store-opt-${l.toLowerCase()}`}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="inline-flex rounded-lg border-2 border-slate-200 overflow-hidden" data-testid="ai-period-toggle">
            {["month", "quarter"].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                data-testid={`ai-period-${p}`}
                className={`px-3 min-h-[40px] text-xs font-bold uppercase tracking-widest ${
                  period === p
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-700 hover:bg-slate-100"
                }`}
              >
                {p === "month" ? "Month · Μήνας" : "Quarter · Τρίμηνο"}
              </button>
            ))}
          </div>

          <Button
            onClick={generate}
            disabled={busy}
            data-testid="ai-generate-btn"
            className="min-h-[40px] bg-indigo-600 hover:bg-indigo-700 text-white font-bold uppercase tracking-widest gap-1.5"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {busy ? "Analyzing…" : report ? "Re-run" : "Generate"}
          </Button>
        </div>
      </div>

      {!report ? (
        <div
          className="px-6 py-10 text-center text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-xl bg-white/60"
          data-testid="ai-empty-state"
        >
          <BarChart3 className="h-10 w-10 mx-auto mb-2 text-indigo-300" />
          Pick a store + period and tap <b>Generate</b>. The AI will scan today&apos;s active
          ledger, supplier expenses and shift recounts to deliver a structured Greek-language report.
        </div>
      ) : (
        <div className="space-y-4">
          {/* Metric chips */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5" data-testid="ai-metric-chips">
            <Chip label="Revenue / Έσοδα" value={`€${m.revenue_total.toFixed(2)}`} tone="emerald" />
            <Chip label="Avg Margin" value={`${m.avg_margin_pct.toFixed(1)}%`} tone="indigo" />
            <Chip
              label="Discrepancy"
              value={`${m.discrepancy_total >= 0 ? "+" : ""}€${m.discrepancy_total.toFixed(2)}`}
              tone={Math.abs(m.discrepancy_total) < 0.01 ? "emerald" : m.discrepancy_total < 0 ? "rose" : "amber"}
            />
            <Chip label="Payouts" value={`€${m.payouts_total.toFixed(2)}`} tone="orange" />
            <Chip label="Cash Drops" value={`€${m.drops_total.toFixed(2)}`} tone="emerald" />
          </div>

          {/* Summary banner */}
          <Section
            icon={<Sparkles className="h-4 w-4" />}
            title="Συνοπτική Εκτίμηση · Summary"
            tone="indigo"
            testId="ai-summary"
          >
            <p className="leading-relaxed text-slate-800 whitespace-pre-line">{r.summary}</p>
          </Section>

          {/* Trio of insight sections */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Section
              icon={<TrendingUp className="h-4 w-4" />}
              title="Revenue & Margin Trends"
              subtitle="Έσοδα & Περιθώριο"
              tone="emerald"
              testId="ai-revenue"
            >
              <p className="text-[13px] leading-relaxed text-slate-800 whitespace-pre-line">{r.revenue_trends}</p>
            </Section>

            <Section
              icon={<AlertTriangle className="h-4 w-4" />}
              title="Expense Audit"
              subtitle="Έλεγχος Προμηθευτών"
              tone="amber"
              testId="ai-expense"
            >
              <p className="text-[13px] leading-relaxed text-slate-800 whitespace-pre-line">{r.expense_audit}</p>
              {m.top_suppliers.length > 0 && (
                <div className="mt-2 pt-2 border-t border-amber-200 space-y-0.5">
                  {m.top_suppliers.slice(0, 3).map((s) => (
                    <div key={s.name} className="flex items-center justify-between text-[11px] font-mono">
                      <span className="truncate text-slate-700">{s.name}</span>
                      <span className="font-bold text-amber-800">€{s.total.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section
              icon={<ShieldAlert className="h-4 w-4" />}
              title="Discrepancy & Fraud"
              subtitle="Αποκλίσεις Ταμείου"
              tone="rose"
              testId="ai-discrepancy"
            >
              <p className="text-[13px] leading-relaxed text-slate-800 whitespace-pre-line">{r.discrepancy_analysis}</p>
              {m.employees_with_deficits.length > 0 && (
                <div className="mt-2 pt-2 border-t border-rose-200 space-y-0.5">
                  {m.employees_with_deficits.slice(0, 3).map((e) => (
                    <div key={e.name} className="flex items-center justify-between text-[11px] font-mono">
                      <span className="truncate text-slate-700">
                        {e.name} · {e.deficits}×
                      </span>
                      <span className="font-bold text-rose-700">€{e.total_short.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>

          {/* Recommendations */}
          {r.recommendations && r.recommendations.length > 0 && (
            <Section
              icon={<Lightbulb className="h-4 w-4" />}
              title="Owner Recommendations"
              subtitle="Προτάσεις προς τον Ιδιοκτήτη"
              tone="violet"
              testId="ai-recommendations"
            >
              <ol className="list-decimal pl-5 space-y-1.5 text-[13px] leading-relaxed text-slate-800">
                {r.recommendations.map((rec, i) => (
                  <li key={i} data-testid={`ai-recommendation-${i}`}>{rec}</li>
                ))}
              </ol>
            </Section>
          )}

          <div className="text-[10px] font-mono text-slate-400 text-right pt-1">
            Generated {new Date(report.generated_at).toLocaleString()} · period from {report.period_start} · source: {report.ai_source}
          </div>
        </div>
      )}
    </Card>
  );
}

const TONE = {
  indigo:  { ring: "border-indigo-200",  iconBg: "bg-indigo-600",  iconColor: "text-white" },
  emerald: { ring: "border-emerald-200", iconBg: "bg-emerald-600", iconColor: "text-white" },
  amber:   { ring: "border-amber-200",   iconBg: "bg-amber-500",   iconColor: "text-white" },
  rose:    { ring: "border-rose-200",    iconBg: "bg-rose-600",    iconColor: "text-white" },
  violet:  { ring: "border-violet-200",  iconBg: "bg-violet-600",  iconColor: "text-white" },
  orange:  { ring: "border-orange-200",  iconBg: "bg-orange-500",  iconColor: "text-white" },
};

function Section({ icon, title, subtitle, tone, testId, children }) {
  const t = TONE[tone] || TONE.indigo;
  return (
    <div className={`bg-white border-2 ${t.ring} rounded-xl p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`h-7 w-7 rounded-md ${t.iconBg} ${t.iconColor} flex items-center justify-center`}>
          {icon}
        </div>
        <div>
          <div className="font-bold text-sm text-slate-900 leading-tight">{title}</div>
          {subtitle && <div className="text-[10px] text-slate-500 leading-tight">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Chip({ label, value, tone }) {
  const t = TONE[tone] || TONE.indigo;
  return (
    <div className={`bg-white border ${t.ring} rounded-lg px-3 py-2`}>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold leading-tight">
        {label}
      </div>
      <div className="font-mono text-base font-bold text-slate-900 leading-tight">{value}</div>
    </div>
  );
}
